/**
 * The entrypoint for loading the lightbox script and styles.
 *
 * @see https://github.com/midzer/tobii
 */

import Tobii from '@midzer/tobii';

const autoplayVideo = true;
const iframeHeight = 480;
const iframeWidth = 780;

/**
 * Parse the video ID for a given Vimeo URL.
 *
 * Vimeo video URLs have one of the following formats:
 * - https://www.vimeo.com/VIDEO_ID
 * - https://vimeo.com/VIDEO_ID
 * - https://player.vimeo.com/video/VIDEO_ID
 * - https://vimeo.com/channels/documentaryfilm/VIDEO_ID
 * - https://vimeo.com/groups/musicvideo/VIDEO_ID
 * - https://vimeo.com/groups/musicvideo/videos/VIDEO_ID
 */
const vimeoPattern = /^(?:https?:\/\/)?(?:www\.|player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/[^/]*\/videos\/|album\/\d+\/video\/|video\/|embed\/|watch\?\S*v=|v\/)?(\d+)/i;

/**
 * Parse the video ID for a given YouTube URL.
 *
 * YouTube video URLs have one of the following formats:
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://youtube.com/watch?v=VIDEO_ID
 * - https://m.youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - https://www.youtube.com/embed/VIDEO_ID
 * - https://www.youtube.com/v/VIDEO_ID
 *
 * In addition to `youtube`, a video can also be loaded from the `youtube-nocookie` domain.
 */
const youtubePattern = /^(?:https?:\/\/)?(?:www\.|m\.)?(?:youtube(?:-nocookie)?\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]vi?=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/i;

/**
 * Parse the ID for a given Matterport URL.
 *
 * Matterport URLs have the following formats:
 * - https://my.matterport.com/show/?m=VIEW_ID
 */
const matterportPattern = /^https:\/\/my\.matterport\.com\/show\/\?m=([a-zA-Z0-9]{11})/i;

/**
 * Keep a map of embeds that have already been processed and
 * trigger the original element. This works around a bug in
 * Tobii's duplicate entry handling that triggers the lightbox
 * but doesn't pass the index of the item within the group.
 *
 * https://github.com/midzer/tobii/issues/114
 */
const processed = {};

/**
 * Return the caption text to use for the given lightbox element.
 *
 * @param {HTMLElement} lightboxElement - An HTML element opening a lightbox.
 * @returns {string} Caption text, if found.
 */
function captionText( lightboxElement ) {
	const imageBlock = lightboxElement.closest( '.wp-block-image' );
	if ( imageBlock ) {
		return imageBlock.querySelector( 'figcaption' )?.innerText || '';
	}

	return '';
}

/**
 * Construct a query string for the given query object.
 *
 * Arguments with `undefined` or `null` as value are omitted.
 *
 * @param {object} queryObject - Query arguments as object.
 * @returns {string} Query string.
 */
function encodeQuery( queryObject ) {
	return Object.entries( queryObject )
		.filter( ( [ , v = null ] ) => v !== null )
		.map( ( entry ) => entry.map( encodeURIComponent ).join( '=' ) )
		.join( '&' );
}

/**
 * If the given element is inside a Gallery block, generate a unique ID for all links to use.
 *
 * @param {HTMLElement} lightboxElement - An HTML element opening a lightbox.
 * @returns {string} Gallery ID.
 */
function getGalleryId( lightboxElement ) {
	const galleryBlock = lightboxElement.closest( '.wp-block-gallery' );
	if ( ! galleryBlock ) {
		return '';
	}

	return [ ...galleryBlock.querySelectorAll( '.wp-block-image img' ) ]
		.map( ( image ) => image.dataset.id ?? '' )
		.filter( Boolean )
		.join( '-' );
}

/**
 * If the given element is an Image from Carousel Quote Slide block, generate a unique ID for all links to use.
 *
 * @param {HTMLElement} lightboxElement - An HTML element opening a lightbox.
 * @returns {string} Image ID.
 */
function getImageId( lightboxElement ) {
	const imageBlock = lightboxElement.closest( '.wp-block-kaplan-carousel.kaplan-carousel--show-thumbnails' );
	if ( ! imageBlock ) {
		return '';
	}

	return [ ...imageBlock.querySelectorAll( '.kaplan-carousel-slide__link source:first-child' ) ]
		.map( ( image ) => image.dataset.id ?? '' )
		.filter( Boolean )
		.join( '-' );
}

/**
 * Transforms span elements inside
 * '.wp-block-kaplan-carousel.kaplan-carousel--show-thumbnails'
 * into anchor links.
 *
 * @returns {void}
 */
function transformSpanToLink() {
	const carouselBlocks = document.querySelectorAll( '.wp-block-kaplan-carousel.kaplan-carousel--show-thumbnails' );

	carouselBlocks.forEach( carouselBlock => {
		const slideImages = carouselBlock.querySelectorAll( '.kaplan-carousel-slide__link' );

		slideImages.forEach( slideImage => {
			if ( slideImage.tagName === 'SPAN' ) {
				const imgElement = slideImage.querySelector( 'img' );
				if ( ! imgElement || ! imgElement.hasAttribute( 'src' ) ) {
					return;
				}

				const imageUrl = imgElement.getAttribute( 'src' );
				const url = new URL( imageUrl );
				const cleanUrl = url.origin + url.pathname;

				const link = document.createElement( 'a' );
				link.classList.add( 'kaplan-carousel-slide__link' );

				const attributes = Array.from( slideImage.attributes );
				attributes.forEach( attr => {
					if ( attr.name !== 'class' ) {
						link.setAttribute( attr.name, attr.value );
						link.href = cleanUrl;
					}
				} );

				link.innerHTML = slideImage.innerHTML;
				slideImage.parentNode.replaceChild( link, slideImage );
			}
		} );
	} );
}

/**
 * Determine all relevant data for configuring the lightbox element.
 *
 * @param {HTMLElement} lightboxElement - An HTML element opening a lightbox.
 * @returns {object} Lightbox data.
 */
function getLightboxData( lightboxElement ) {
	const lightboxData = {
		dataset: {},
	};

	// transformSpanToLink();
	const { href } = lightboxElement;

	const imageId = getImageId( lightboxElement );
	if ( imageId ) {
		lightboxData.dataset.group = `image-${ imageId }`;
		lightboxData.type = 'image';
	}

	const youtubeId = youtubePattern.exec( href )?.[ 1 ];
	if ( youtubeId ) {
		lightboxData.dataset.type = 'iframe';
		lightboxData.dataset.target = `https://www.youtube.com/embed/${ youtubeId }?rel=0&autoplay=1`;
		lightboxData.dataset.width = iframeWidth;
		lightboxData.dataset.height = iframeHeight;
		lightboxData.dataset.group = 'iframe-youtube';
		lightboxData.type = 'video';
	}

	const vimeoId = vimeoPattern.exec( href )?.[ 1 ];
	if ( vimeoId ) {
		const queryObject = {
			// If present, the hash needs to be the first argument!
			h: /[?&]h=([^&$]+)[&$]/.exec( href )?.[ 1 ],
			autoplay: Number( autoplayVideo ),
			loop: 0,
			portrait: 0,
			byline: 0,
			title: 0,
		};
		lightboxData.dataset.type = 'iframe';
		lightboxData.dataset.target = `https://player.vimeo.com/video/${ vimeoId }?${ encodeQuery( queryObject ) }`;
		lightboxData.dataset.width = iframeWidth;
		lightboxData.dataset.height = iframeHeight;
		lightboxData.dataset.group = `vimeo-${ vimeoId }`;
		lightboxData.type = 'video';
	}

	const galleryId = getGalleryId( lightboxElement );
	if ( galleryId ) {
		lightboxData.dataset.group = `gallery-${ galleryId }`;
		lightboxData.type = 'gallery';
	}

	const matterportId = matterportPattern.exec( href )?.[ 1 ];
	if ( matterportId ) {
		lightboxData.dataset.type = 'iframe';
		lightboxData.dataset.target = href;
		lightboxData.dataset.width = iframeWidth;
		lightboxData.dataset.height = iframeHeight;
		lightboxData.dataset.group = `matterport-${ matterportId }`;
		lightboxData.type = '360view';
	}

	return lightboxData;
}

/**
 * Add a "View image gallery" link to the gallery block that contains the given element.
 *
 * @param {HTMLElement} lightboxElement - An HTML element opening a lightbox.
 */
function addViewLink( lightboxElement ) {
	const gallery = lightboxElement.closest( '.wp-block-gallery' );
	if ( ! gallery ) {
		// This should not happen!
		return;
	}

	if ( gallery.querySelector( 'a.view-image-gallery' ) ) {
		// Link has already been added.
		return;
	}

	const link = document.createElement( 'a' );
	link.classList.add( 'view-image-gallery', 'lightbox' );
	link.dataset.group = lightboxElement.dataset.group;
	link.href = lightboxElement.href;
	link.innerText = 'View image gallery';

	gallery.appendChild( link );
}

/**
 * Add a custom class to images inside lightbox links to automatically display a play icon.
 *
 * @param {HTMLElement} lightboxElement - An HTML element opening a lightbox.
 */
function ensurePlayIcon( lightboxElement ) {
	if ( ! lightboxElement.querySelectorAll( 'img' ).length ) {
		return;
	}

	lightboxElement.classList.add( 'has-play-icon' );

	if ( ! lightboxElement.style.display ) {
		lightboxElement.style.display = 'inline-block';
	}
}

/**
 * Add a custom class to images inside lightbox links to automatically display a play icon.
 *
 * @param {HTMLElement} lightboxElement - An HTML element opening a lightbox.
 */
function ensure360Icon( lightboxElement ) {
	if ( ! lightboxElement.querySelectorAll( 'img' ).length ) {
		return;
	}

	lightboxElement.classList.add( 'has-360-icon' );

	if ( ! lightboxElement.style.display ) {
		lightboxElement.style.display = 'inline-block';
	}

	/**
	 * Find the thumbnail button if the link is in a carousel slide
	 * and apply the same class to it.
	 */
	const parentSlide = lightboxElement.closest( '.kaplan-carousel-slide' );
	if ( ! parentSlide ) {
		return;
	}

	const thumbnailButton = document.querySelector( `button[aria-controls="${ parentSlide.id }"]` );
	if ( ! thumbnailButton ) {
		return;
	}

	thumbnailButton.classList.add( 'has-360-icon' );
}

/**
 * Prepare the lightbox element and ensure all relevant data is in place.
 *
 * @param {HTMLElement} lightboxElement - An HTML element opening a lightbox.
 */
function prepareLightbox( lightboxElement ) {
	const { type, ...lightboxData } = getLightboxData( lightboxElement );

	if ( ! type ) {
		return;
	}

	const { href } = lightboxElement;

	// Trigger original instance on click if found.
	if ( processed[ href ] ) {
		lightboxElement.addEventListener( 'click', event => {
			event.preventDefault();
			processed[ href ].click();
		} );
		return;
	}

	processed[ href ] = lightboxElement;

	lightboxElement.classList.add( 'lightbox' );

	Object.entries( lightboxData.dataset ).forEach( ( [ name, value ] ) => {
		lightboxElement.dataset[ name ] = value;
	} );

	switch ( type ) {
		case 'gallery':
			addViewLink( lightboxElement );
			break;

		case 'video':
			ensurePlayIcon( lightboxElement );
			break;

		case '360view':
			ensure360Icon( lightboxElement );
			break;

		default:
	}
}

transformSpanToLink();

const lightboxElements = document.querySelector( '.site' )?.querySelectorAll( 'a[href]' ) ?? [];
for ( const lightboxElement of lightboxElements ) {
	prepareLightbox( lightboxElement );
}

new Tobii( {
	autoplayVideo,
	captionText,
	closeText: null,
	nav: true,
	navText: [
		'',
		'',
	],
	zoom: false,
} );
